import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Descriptions, 
  Button, 
  Spin,
  Alert,
  Tag,
  List
} from 'antd';
import { 
  ArrowLeftOutlined, 
  SecurityScanOutlined, 
  TeamOutlined,
  EditOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import API from '../../API';
import { notifyError } from '../../Util';

const { Title, Text } = Typography;

/**
 * @component
 * @name RoleDetailView
 * @description Detailed view of a single role with all its information
 */
const RoleDetailView = () => {
  const { roleId } = useParams();
  const history = useHistory();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const systemRoles = ['admin', 'pit', 'pct', 'pit-focal-person', 'pct-focal-person'];

  const isSystemRole = (roleName) => {
    return systemRoles.includes(roleName);
  };

  useEffect(() => {
    loadRoleDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const loadRoleDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
  // Try to get role by ID
  const payload = await API.get(`roles/${roleId}`);
  // API.get returns response.data.data, which for detail endpoints is the role object itself
  // Handle defensively in case of shape variations
  setRole(payload && payload.data ? payload.data : payload);
    } catch (err) {
      console.error('Error loading role details:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load role details';
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleEdit = () => {
    // Navigate back to roles list with edit mode
    history.push('/roles', { editRoleId: roleId });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
          >
            Back
          </Button>
        </div>
        <Alert
          message="Error Loading Role"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!role) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
          >
            Back
          </Button>
        </div>
        <Alert
          message="Role Not Found"
          description="The requested role could not be found."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          type="text"
        >
          Back to Roles
        </Button>
        
        {!isSystemRole(role.name) && (
          <Button 
            icon={<EditOutlined />} 
            onClick={handleEdit}
            type="primary"
          >
            Edit Role
          </Button>
        )}
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SecurityScanOutlined style={{ marginRight: '8px', fontSize: '20px' }} />
                Role Information
              </div>
            } 
            bordered={false}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div 
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  backgroundColor: '#722ed1', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto',
                  marginBottom: '16px'
                }}
              >
                <SecurityScanOutlined style={{ fontSize: '36px', color: 'white' }} />
              </div>
              <Title level={3} style={{ marginBottom: '4px' }}>
                {role.name}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {role.description || 'No description provided'}
              </Text>
            </div>
            
            <Descriptions column={1} bordered size="middle">
              <Descriptions.Item label="Role Name">
                {role.name}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {role.description || 'No description provided'}
              </Descriptions.Item>
              <Descriptions.Item label="Guard Name">
                {role.guard_name || 'web'}
              </Descriptions.Item>
              <Descriptions.Item label="Total Permissions">
                <Tag color="blue">
                  {role.permissions ? role.permissions.length : 0} permissions
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total Users">
                <Tag color="green">
                  {role.users ? role.users.length : 0} users
                </Tag>
              </Descriptions.Item>
              {role.created_at && (
                <Descriptions.Item label={<><CalendarOutlined /> Created</>}>
                  {new Date(role.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Descriptions.Item>
              )}
              {role.updated_at && (
                <Descriptions.Item label="Last Updated">
                  {new Date(role.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Permissions Card */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <SecurityScanOutlined style={{ marginRight: '8px' }} />
                Permissions ({role.permissions ? role.permissions.length : 0})
              </div>
            } 
            bordered={false}
          >
            {role.permissions && role.permissions.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {role.permissions.map((permission) => (
                  <Tag 
                    key={permission.id} 
                    style={{ 
                      marginBottom: '8px', 
                      display: 'inline-block',
                      maxWidth: '100%'
                    }}
                    color="blue"
                  >
                    {permission.name}
                  </Tag>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <SecurityScanOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Text type="secondary">No permissions assigned to this role</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Users with this Role Card */}
      {role.users && role.users.length > 0 && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <TeamOutlined style={{ marginRight: '8px' }} />
                  Users with this Role ({role.users.length})
                </div>
              } 
              bordered={false}
            >
              <List
                grid={{ 
                  gutter: 16, 
                  xs: 1, 
                  sm: 2, 
                  md: 3, 
                  lg: 4, 
                  xl: 5, 
                  xxl: 6 
                }}
                dataSource={role.users}
                renderItem={(user) => (
                  <List.Item>
                    <Card 
                      size="small" 
                      hoverable
                      onClick={() => history.push(`/users/${user.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Meta
                        title={
                          <div style={{ fontSize: '14px' }}>
                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                          </div>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {user.email}
                          </Text>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Additional Role Metadata */}
      {(role.guard_name || role.id) && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Card title="Technical Details" bordered={false}>
              <Descriptions column={3} bordered size="small">
                <Descriptions.Item label="Role ID">
                  {role.id}
                </Descriptions.Item>
                <Descriptions.Item label="Guard Name">
                  {role.guard_name || 'web'}
                </Descriptions.Item>
                <Descriptions.Item label="Internal Name">
                  {role.name}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default RoleDetailView;